import React, { useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import './ProviderUnderReview.css';

const ProviderUnderReviewContent: React.FC = () => {
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const { user, isProviderUnderReview, isProviderActive } = useAuth();

  // Debug logging
  useEffect(() => {
    console.log('🔍 ProviderUnderReview component loaded:', {
      user: user?.username,
      status: user?.status,
      roles: user?.roles,
      isProviderUnderReview: isProviderUnderReview(),
      isProviderActive: isProviderActive(),
      translationReady: ready,
      currentLanguage: t('language.english'), // Test translation
      testTranslation: t('providerUnderReview.pageTitle')
    });
  }, [user, isProviderUnderReview, isProviderActive, ready, t]);

  // Show loading state while translations are loading
  if (!ready) {
    return (
      <div className="provider-under-review">
        <div className="under-review-container" style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loading-spinner">Loading translations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-under-review">
      <div className="under-review-container">
        {/* Header */}
        <div className="under-review-header">
          <div className="review-status-icon">
            <div className="clock-icon">⏳</div>
          </div>
          <h1>{t('providerUnderReview.pageTitle')}</h1>
          <p className="welcome-text">
            {t('providerUnderReview.welcomeText', { name: user?.firstName || user?.username || 'Provider' })}
          </p>
        </div>

        {/* Status Card */}
        <div className="status-card">
          <div className="status-badge">
            <span className="status-dot"></span>
            {t('providerUnderReview.statusBadge')}
          </div>
          
          <div className="status-content">
            <div className="content-left">
              <h2>{t('providerUnderReview.mainTitle')}</h2>
              <p>
                {t('providerUnderReview.mainDescription')}
              </p>
              
              <div className="review-info">
                <div className="info-item">
                  <span className="info-icon">⏰</span>
                  <span>{t('providerUnderReview.reviewInfo.timeline')}</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">📧</span>
                  <span>{t('providerUnderReview.reviewInfo.notification')}</span>
                </div>
                <div className="info-item">
                  <span className="info-icon">🔒</span>
                  <span>{t('providerUnderReview.reviewInfo.limitedAccess')}</span>
                </div>
              </div>
            </div>
            
            <div className="content-right">
              <div className="review-timeline">
                <div className="timeline-item completed">
                  <div className="timeline-icon">✅</div>
                  <div className="timeline-content">
                    <h4>{t('providerUnderReview.timeline.registration.title')}</h4>
                    <p>{t('providerUnderReview.timeline.registration.description')}</p>
                  </div>
                </div>
                
                <div className="timeline-item active">
                  <div className="timeline-icon">🔍</div>
                  <div className="timeline-content">
                    <h4>{t('providerUnderReview.timeline.verification.title')}</h4>
                    <p>{t('providerUnderReview.timeline.verification.description')}</p>
                  </div>
                </div>
                
                <div className="timeline-item pending">
                  <div className="timeline-icon">⭐</div>
                  <div className="timeline-content">
                    <h4>{t('providerUnderReview.timeline.activation.title')}</h4>
                    <p>{t('providerUnderReview.timeline.activation.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="action-cards">
          <div className="action-card primary">
            <div className="card-icon">📝</div>
            <div className="card-content">
              <h3>{t('providerUnderReview.actionCards.completeProfile.title')}</h3>
              <p>
                {t('providerUnderReview.actionCards.completeProfile.description')}
              </p>
              <button 
                className="card-action-btn primary"
                onClick={() => navigate('/provider/profile')}
              >
                {t('providerUnderReview.actionCards.completeProfile.button')}
              </button>
            </div>
          </div>

          <div className="action-card">
            <div className="card-icon">📋</div>
            <div className="card-content">
              <h3>{t('providerUnderReview.actionCards.whatsNext.title')}</h3>
              <div className="next-steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <span>{t('providerUnderReview.actionCards.whatsNext.steps.step1')}</span>
                </div>
                <div className="step">
                  <span className="step-number">2</span>
                  <span>{t('providerUnderReview.actionCards.whatsNext.steps.step2')}</span>
                </div>
                <div className="step">
                  <span className="step-number">3</span>
                  <span>{t('providerUnderReview.actionCards.whatsNext.steps.step3')}</span>
                </div>
                <div className="step">
                  <span className="step-number">4</span>
                  <span>{t('providerUnderReview.actionCards.whatsNext.steps.step4')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Restricted Access Notice */}
        <div className="restricted-notice">
          <div className="notice-header">
            <span className="notice-icon">🔒</span>
            <h3>{t('providerUnderReview.restrictedAccess.title')}</h3>
          </div>
          <p>
            {t('providerUnderReview.restrictedAccess.description')}
          </p>
          <div className="restricted-features">
            <div className="feature-item">
              <span className="feature-icon">❌</span>
              <span>{t('providerUnderReview.restrictedAccess.features.serviceManagement')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">❌</span>
              <span>{t('providerUnderReview.restrictedAccess.features.calendarManagement')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">❌</span>
              <span>{t('providerUnderReview.restrictedAccess.features.bookingManagement')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">❌</span>
              <span>{t('providerUnderReview.restrictedAccess.features.dashboardAnalytics')}</span>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="support-section">
          <h3>{t('providerUnderReview.support.title')}</h3>
          <p>
            {t('providerUnderReview.support.description')}
          </p>
          <div className="support-actions">
            {/* <button className="support-btn">
              📧 Contact Support
            </button> */}
            <button 
              className="support-btn secondary"
              onClick={() => window.open('tel:+91 9217588609')}
            >
              {t('providerUnderReview.support.callSupport')}
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="footer-actions">
          <button 
            className="logout-btn"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/';
            }}
          >
            {t('providerUnderReview.logout')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProviderUnderReview: React.FC = () => {
  return (
    <Suspense fallback={
      <div className="provider-under-review">
        <div className="under-review-container" style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    }>
      <ProviderUnderReviewContent />
    </Suspense>
  );
};

export default ProviderUnderReview;